/**
 * @file Classes of the nodes in the node model and the root.
 */

import { DiagramTreeNode } from './diagram-tree-model';

enum NodeType {
  Object = 'object',
  Process = 'process',
  State = 'state',
  Value = 'value',
}

/**
 * 子节点分组类型 - 用于区分父节点包含的子节点类型
 * - None: 未确定类型（新节点默认值，可以添加状态或值）
 * - State: 状态分组，只能添加状态子节点
 * - Value: 值分组，只能添加值子节点，标签显示时添加 [] 后缀
 * - InZoom: 展开子图分组，只能添加对象和过程子节点
 */
enum ChildGroupType {
  /** 未确定类型 - 新节点默认值，可以添加状态或值 */
  None = 'none',
  /** 状态分组 - 只能添加状态子节点 */
  State = 'state',
  /** 值分组 - 只能添加值子节点，标签显示时添加 [] 后缀 */
  Value = 'value',
  /** 展开子图分组 - 只能添加对象和过程子节点 */
  InZoom = 'inzoom',
}

enum Essence {
  Informatical = 'no',
  Physical = 'yes',
}

enum Affiliation {
  Systemic = 'solid',
  Environmental = 'dashed',
}

type ProcessAutonomy = {
  action: string;
  prompt: string;
  withTool: boolean;
};

class MMNode {
  id: string;
  parent: MMNode | MMRoot | null;
  children: Array<MMNode>;
  type: 'object' | 'process' | 'state' | 'value';
  label: string;
  // 节点描述（可选，用于与 OPMEditor 兼容并支持扩展属性）
  description: string;
  // 扩展字段（与描述同级，便于保存/导出）
  namespace?: string;
  packageName?: string;
  codeName?: string;
  tags: string[];
  objectType?: string;
  exampleValues?: string;
  version?: string;
  updatedAt?: string;
  updatedBy?: string[];
  relatedSystem?: string;
  autonomy?: ProcessAutonomy;
  // 临时编辑标记（仅前端使用，不参与导出）
  __opmcvDirty?: boolean;
  __opmcvEditSnapshot?: {
    label: string;
    codeName: string;
    description: string;
    tags: string[];
    objectType: string;
    exampleValues: string;
    packageName?: string;
    autonomy?: ProcessAutonomy;
  };
  diagram: DiagramTreeNode | null;
  deleted: boolean;
  essence: Essence;
  affiliation: Affiliation;
  isStructurePart: boolean;
  isSubelementOfMain: boolean;
  // 节点尺寸字段，用于持久化
  width: number | null;
  height: number | null;
  // 子节点分组类型，用于区分包含状态还是值
  childGroupType: ChildGroupType;
  // Process节点的函数定义（仅用于process类型）
  functionDefinition?: {
    signature: string;
    code: string;
    description: string;
  };
  // Process节点的执行记录（仅用于process类型）
  executionRecords?: Array<{
    id: string;
    timestamp: string;
    input: string;
    output: string;
    status: 'success' | 'error';
    duration: number;
  }>;

  constructor(
    id: string,
    type: 'object' | 'process' | 'state' | 'value',
    label: string
  ) {
    this.id = id;
    this.label = label;
    this.description = '';
    // 初始化扩展字段
    this.namespace = '';
    this.packageName = '';
    this.codeName = '';
    this.tags = [];
    this.objectType = type === 'object' ? 'composite' : undefined;
    this.exampleValues = '';
    this.version = type === 'object' || type === 'process' ? '1.0' : undefined;
    this.updatedAt = '';
    this.updatedBy = [];
    this.relatedSystem = '';
    this.autonomy =
      type === 'process'
        ? {
            action: '',
            prompt: '',
            withTool: false,
          }
        : undefined;
    this.type = type;
    this.parent = null;
    this.children = [];
    this.diagram = null;
    this.deleted = false;
    this.essence = Essence.Informatical;
    this.affiliation = Affiliation.Systemic;
    this.isStructurePart = false;
    this.isSubelementOfMain = false;
    // 初始化尺寸为 null，表示使用默认尺寸
    this.width = null;
    this.height = null;
    // 默认为未确定类型，可以添加状态或值
    this.childGroupType = ChildGroupType.None;
    // 初始化Process节点的函数定义和执行记录
    if (type === 'process') {
      this.functionDefinition = undefined;
      this.executionRecords = [];
    }
  }

  /**
   * Add a given node to the .children array. Set this as the child's parent,
   * @param child - Model node to be added
   */
  addChild(child: MMNode) {
    this.children.push(child);
    child.parent = this;
  }

  /**
   * Remove a child from the .children array.
   * @param child Model node to be removed
   */
  removeChild(child: MMNode) {
    let index = this.children.indexOf(child);
    if (index !== -1) {
      this.children.splice(index, 1);
    }
  }
}

class MMRoot {
  children: Array<MMNode>;

  constructor() {
    this.children = [];
  }

  /**
   * Add a given node to the .children array. Set this as the child's parent
   * @param child - Model node to be added
   */
  addChild(child: MMNode) {
    this.children.push(child);
    child.parent = this;
  }

  /**
   * Remove a child from the .children array.
   * @param child Model node to be removed
   */
  removeChild(child: MMNode) {
    let index = this.children.indexOf(child);
    if (index !== -1) {
      this.children.splice(index, 1);
    }
  }
}
let masterModelRoot = new MMRoot();

//used when importing from JSON
const importMMRoot = (newRoot: MMRoot) => {
  masterModelRoot = newRoot;
};

// 重置为空的 master model（新建本体图时调用）
const resetMasterModelRoot = () => {
  masterModelRoot = new MMRoot();
};

export {
  masterModelRoot,
  MMNode,
  MMRoot,
  NodeType,
  importMMRoot,
  resetMasterModelRoot,
  Essence,
  Affiliation,
  ChildGroupType,
  type ProcessAutonomy,
};
